import { useCallback, useEffect, useRef, useState } from 'react';
import CustomCkEditorInstance from 'ckeditor-custom-build';
import type { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';

interface WordCount {
  words: number;
  characters: number;
}

interface UseSwivuRichEditorReturn {
  initialize: (ref: HTMLElement) => Promise<void>;
  setNoteData: (data: string) => void;
  clearNoteData: () => void;
  setEditorReadOnly: (isReadOnly: boolean) => void;
  markAsSaved: () => void;
  noteData: string;
  wordCount: WordCount;
  hasChanges: boolean;
  isInitialized: boolean;
  isEmpty: boolean;
  handleUninitialize: () => void;
  editorInstance: ClassicEditor | null;
}

export const useSwivuRichEditor = ({ height }: { height?: string | number } = {}): UseSwivuRichEditorReturn => {
  const [noteData, setNoteDataState] = useState<string>('');
  const [wordCount, setWordCount] = useState<WordCount>({ words: 0, characters: 0 });
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);
  const editorInstanceRef = useRef<ClassicEditor | null>(null);
  const wordCountListenerRef = useRef<(() => void) | null>(null);
  const initialDataRef = useRef<string>('');

  // Helper function to check if content is empty
  const checkIfEmpty = useCallback((data: string) => {
    // Remove HTML tags and check if there's any meaningful content
    const textContent = data.replace(/<[^>]*>/g, '').trim();
    return textContent === '';
  }, []);

  const initialize = useCallback(async (ref: HTMLElement): Promise<void> => {
    if (!ref || !(ref instanceof HTMLElement) || editorInstanceRef.current) return;

    try {
      const editor = await CustomCkEditorInstance.create(ref, {
        toolbar: {
          items: [
            'bold',
            'underline',
            'italic',
            'strikethrough',
            '|',
            'fontSize',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            '|',
            'imageInsert',
            'link',
          ]
        },
      });
      editorInstanceRef.current = editor;
      setIsInitialized(true);

      editor.ui.view.element?.classList.add("swivu-modern-ck-editor");

      // Add swivu-modern-ck-editor class to balloon panels (image toolbars)
      // Access the contextual balloon plugin which manages image toolbars
      const contextualBalloon = editor.plugins.get('ContextualBalloon');

      // Listen for when balloon panels are shown and add our class to the view's class property
      contextualBalloon.on('change:visibleView', () => {
        const balloonView = contextualBalloon.view;
        if (balloonView && balloonView.element && balloonView.element instanceof HTMLElement) {
          const currentClass = balloonView.class || '';
          if (!currentClass.includes('swivu-modern-ck-editor')) {
            balloonView.class = currentClass + ' swivu-modern-ck-editor';

            // Only append if the element is not already in the DOM and parent exists
            if (ref.parentElement && !ref.parentElement.contains(balloonView.element)) {
              ref.parentElement.append(balloonView.element);
            }
          }
        }
      });

      // Set up word count listener
      const wordCountPlugin = editor.plugins.get('WordCount');
      const updateWordCount = () => {
        setWordCount({
          words: wordCountPlugin.words,
          characters: wordCountPlugin.characters
        });
      };

      wordCountListenerRef.current = updateWordCount;
      wordCountPlugin.on('update', updateWordCount);

      // Initial word count
      updateWordCount();

      // Store initial data for change detection
      initialDataRef.current = editor.getData();

      // Focus the editor
      editor.editing.view.focus();

      editor.editing.view.change((writer) => {
        const root = editor?.editing?.view?.document?.getRoot();
        if (height) {
          // @ts-expect-error - editor is not typed
          writer.setStyle("height", `${height}px`, root);
        } else {
          const toolbarHeight = editor?.ui?.view?.toolbar?.element?.clientHeight || 0;
          // @ts-expect-error - editor is not typed
          writer.setStyle("height", `${ref.parentElement?.offsetHeight - toolbarHeight}px`, root);
        }
      });
    } catch (error) {
      console.error('Failed to initialize CKEditor:', error);
      throw error;
    }
  }, []);

  const setNoteData = useCallback((data: string) => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setData(data);
      setNoteDataState(data);
      // Update initial data reference when setting new data
      initialDataRef.current = data;
      setHasChanges(false);
      setIsEmpty(checkIfEmpty(data));
    }
  }, [checkIfEmpty]);

  const clearNoteData = useCallback(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setData('');
      setNoteDataState('');
      // Update initial data reference when clearing
      initialDataRef.current = '';
      setHasChanges(false);
      setIsEmpty(true);
    }
  }, []);

  const setEditorReadOnly = useCallback((isReadOnly: boolean) => {
    if (editorInstanceRef.current) {
      const editor = editorInstanceRef.current;

      if (isReadOnly) {
        editor.enableReadOnlyMode('swivu-editor-readonly');
        if (editor.ui.view.toolbar.element) {
          editor.ui.view.toolbar.element.style.display = "none";
        }
      } else {
        editor.disableReadOnlyMode('swivu-editor-readonly');
        if (editor.ui.view.toolbar.element) {
          editor.ui.view.toolbar.element.style.display = "flex";
        }
      }
    }
  }, []);

  const markAsSaved = useCallback(() => {
    if (editorInstanceRef.current) {
      initialDataRef.current = editorInstanceRef.current.getData();
      setHasChanges(false);
    }
  }, []);

  // Update noteData when editor content changes
  useEffect(() => {
    if (editorInstanceRef.current && isInitialized) {
      const editor = editorInstanceRef.current;

      const handleDataChange = () => {
        const data = editor.getData();
        setNoteDataState(data);
        // Check if content has changed from initial data
        setHasChanges(data !== initialDataRef.current);
        setIsEmpty(checkIfEmpty(data));
      };

      editor.model.document.on('change:data', handleDataChange);

      return () => {
        editor.model.document.off('change:data', handleDataChange);
      };
    }
    return undefined;
  }, [isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editorInstanceRef.current) {
        try {
          if (wordCountListenerRef.current) {
            const wordCountPlugin = editorInstanceRef.current.plugins.get('WordCount');
            wordCountPlugin.off('update', wordCountListenerRef.current);
          }
          editorInstanceRef.current.destroy();
        } catch (error) {
          console.warn('Error during CKEditor cleanup:', error);
        } finally {
          editorInstanceRef.current = null;
          setIsInitialized(false);
        }
      }
    };
  }, []);

  const handleUninitialize = useCallback(() => {
    setIsInitialized(false);
    editorInstanceRef.current = null;
  }, []);

  return {
    initialize,
    setNoteData,
    clearNoteData,
    setEditorReadOnly,
    markAsSaved,
    noteData,
    wordCount,
    hasChanges,
    isInitialized,
    isEmpty,
    handleUninitialize,
    editorInstance: editorInstanceRef.current
  };
};